import { useEffect, useState } from "react";


import { BigNumbers } from "../bigNumbers";
import { OccurrenceWithTimeSpent } from "../../../../models/occurrence";
import { OccurrenceStepEnum } from "../../../../enums/occurrenceStep";
import { OverlayTrigger, Tooltip } from "@rio-cloud/rio-uikit";
import { QuantityChart } from "./quantityChart";
import { AverageTimeChart } from "./averageTimeChart";
import { useKanbanCharts } from "../../../../hooks/useKanban";

export interface ChartsProps {
  occurrenceStepAvailable: string[];
  setChassisListFilter: (chassisListFilter: string[]) => void;
  loading?: boolean;
  assetIds?: string[];
  setStepFilter: (stepFilter: OccurrenceStepEnum | null) => void;
  stepFilter: OccurrenceStepEnum | null;
  occurrenceWithTimeSpentList: OccurrenceWithTimeSpent[];
  finalizedOccurrenceWithTimeSpentList: OccurrenceWithTimeSpent[];
  totalOccurrences: number;
  totalInProgress: number;
  firstLoad: boolean;
  showFinalized: boolean;
  chassisListFilterByStep: string[];
  setChassisListFilterByStep: (chassisListFilterByStep: string[]) => void;
  finishedFilter: string[];
  setFinishedFilter: (finished: string[]) => void;
  updatedFilters: number;
  isCurrentStageMode: boolean;
  setIsCurrentStageMode: (v: boolean) => void;
  currentStepFilter: OccurrenceStepEnum | null;
  setCurrentStepFilter: (step: OccurrenceStepEnum | null) => void;
  currentStageBaseList?: OccurrenceWithTimeSpent[];
}

const useWindowWidth = () => {
  const [width, setWidth] = useState(window.innerWidth);
  useEffect(() => {
    const handler = () => setWidth(window.innerWidth);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  return width;
};

export const Charts = (props: ChartsProps) => {

  const windowWidth = useWindowWidth();
  const btnFontSize = windowWidth < 1024 ? 13 : 14;
  const btnPadding = windowWidth < 640 ? '2px 6px' : windowWidth < 1024 ? '3px 10px' : '6px 16px';
  const btnStyle: React.CSSProperties = { fontSize: btnFontSize, padding: btnPadding };

  const {
    containerWidth, LEFT_LABEL_PAD, isResponsiveView, chartWidthMultiplier, defaultChartMinWidth,
    dynamicBarSize, showAverageTimeChart, setShowAverageTimeChart, showQuantityChart, setShowQuantityChart, selectedMetric,
    setSelectedMetric, activeStepTab, setActiveStepTab, timePeriod, setTimePeriod, currentOccurrencesBySteps, finalizedOccurrencesBySteps,
    allOccurrencesBySteps, occurrencesByDays, adjustDataWithMinHeight, getBigNumbers,
  } = useKanbanCharts(props);

  useEffect(() => {
    props.setStepFilter(null);
    props.setChassisListFilterByStep([]);
    setActiveStepTab('');
  }, [props.updatedFilters]);

  const contentWithTooltip = (content: any) => {
    return (
      <OverlayTrigger
        placement={"top"}
        overlay={
          <Tooltip id="tooltip" allowOnTouch>
            <div className="flex flex-col padding-10">{content}</div>
          </Tooltip>
        }
      >
        <div className="truncate">{content}</div>
      </OverlayTrigger>
    );
  };

  const CustomTick = ({ x, y, payload }: any) => {
    const label = payload.value;
    const truncated = label;

    return (
      <g transform={`translate(${x},${y})`}>
        <foreignObject x={-30} y={0} width={60} height={20}>
          {contentWithTooltip(truncated)}
        </foreignObject>
      </g>
    );
  };
      
  return (
    <>
      <div
        className="flex justify-between gap-16 bg-[#E8EDF3] rounded p-4 kanban-bn-background"
        style={{ flexDirection: windowWidth < 1024 ? 'column' : 'row', alignItems: windowWidth < 1024 ? 'flex-start' : 'center' }}
      >

        <div
          className="flex gap-3 flex-shrink-0"
          style={{ flexDirection: windowWidth <= 768 ? 'column' : 'row', alignItems: 'center' }}
        >
          <div className="flex flex-col mr-6">
            <div className="flex">
              <button
                className={`btn rounded-br-none rounded-bl-none rounded-tr-none ${
                  !props.isCurrentStageMode && props.finishedFilter.length === 1 && props.finishedFilter[0] === 'false' ? "btn-primary" : "btn-default"
                }`}
                style={btnStyle}
                onClick={() => {
                  props.setIsCurrentStageMode(false);
                  props.setCurrentStepFilter(null);
                  props.setFinishedFilter(['false']);
                  props.setStepFilter(null);
                  props.setChassisListFilterByStep([]);
                  setActiveStepTab('');
                }}
              >
                Em aberto
              </button>
              <button
                className={`btn rounded-bl-none rounded-br-none rounded-tl-none border-l-0 ${
                  !props.isCurrentStageMode && props.finishedFilter.length === 1 && props.finishedFilter[0] === 'true' ? "btn-primary" : "btn-default"
                }`}
                style={btnStyle}
                onClick={() => {
                  props.setIsCurrentStageMode(false);
                  props.setCurrentStepFilter(null);
                  props.setFinishedFilter(['true']);
                  props.setStepFilter(null);
                  props.setChassisListFilterByStep([]);
                  setActiveStepTab('');
                }}
              >
                Concluídos
              </button>
            </div>
            <button
              className={`btn rounded-tl-none rounded-tr-none border-t-0 w-full ${
                props.isCurrentStageMode ? "btn-primary" : "btn-default"
              }`}
              style={btnStyle}
              onClick={() => {
                  props.setIsCurrentStageMode(true);
                props.setCurrentStepFilter(null);
                props.setFinishedFilter(['false']);
                props.setStepFilter(null);
                props.setChassisListFilterByStep([]);
                setActiveStepTab('');
              }}
            >
              Etapa atual
            </button>
          </div>
          
          <div className="flex items-center">
            <button
              className={`btn rounded-tr-none rounded-br-none ${selectedMetric === "average" ? "btn-primary" : "btn-default"}`}
              style={btnStyle}
              onClick={() => {
                setSelectedMetric("average");
                props.setStepFilter(null);
                props.setChassisListFilterByStep([]);
                setActiveStepTab('');
              }}
            >
              Média de tempo
            </button>
            <button
              className={`btn rounded-tl-none rounded-bl-none ${selectedMetric === "quantity" ? "btn-primary" : "btn-default"}`}
              style={btnStyle}
              onClick={() => {
                setSelectedMetric("quantity");
                props.setStepFilter(null);
                props.setChassisListFilterByStep([]);
                setActiveStepTab('');
              }}
            >
              Quantidade
            </button>
          </div>
        </div>

        <div style={{ minWidth: 0, width: windowWidth < 1024 ? '100%' : undefined, justifyContent: 'flex-end' }} className="flex">
          <BigNumbers
            bigNumberAnalytics={getBigNumbers()}
            finishedFilter={props.finishedFilter}
            loading={props.loading}
            isFilteredByStep={props.stepFilter !== null || props.chassisListFilterByStep.length > 0 || props.isCurrentStageMode}
          />
        </div>
      </div>

      <div className="bg-white shadow rounded text-sm p-4 overflow-hidden mt-3 kanban-charts">
        {selectedMetric === "average" && (
          <AverageTimeChart
            showAverageTimeChart={showAverageTimeChart}
            setShowAverageTimeChart={setShowAverageTimeChart}
            activeTab={activeStepTab}
            setActiveTab={(activeStepTab: string | null) => setActiveStepTab(activeStepTab ?? '')}
            contentWithTooltip={contentWithTooltip}
            occurrencesBySteps={props.isCurrentStageMode || props.stepFilter ? currentOccurrencesBySteps : allOccurrencesBySteps}
            finalizedOccurrencesBySteps={props.showFinalized ? finalizedOccurrencesBySteps : undefined}
            adjustDataWithMinHeight={adjustDataWithMinHeight}
            isResponsiveView={isResponsiveView}
            chartWidthMultiplier={chartWidthMultiplier}
            defaultChartMinWidth={defaultChartMinWidth}
            dynamicBarSize={dynamicBarSize}
            CustomTick={CustomTick}
            occurrenceStepAvailable={props.occurrenceStepAvailable}
            stepFilter={props.stepFilter}
            setChassisListFilter={props.setChassisListFilter}
            setStepFilter={props.setStepFilter}
            loading={props.loading}
            firstLoad={props.firstLoad}
            setChassisListFilterByStep={props.setChassisListFilterByStep}
            finishedFilter={props.finishedFilter}
            divideByUniqueChassis
            isCurrentStageMode={props.isCurrentStageMode}
            setCurrentStepFilter={props.setCurrentStepFilter}
          />
        )}

        {selectedMetric === "quantity" && (
          <QuantityChart
            activeTab={activeStepTab}
            setActiveTab={(activeStepTab: string | null) => setActiveStepTab(activeStepTab ?? '')}
            contentWithTooltip={contentWithTooltip}
            occurrencesByDays={occurrencesByDays}
            occurrencesBySteps={currentOccurrencesBySteps}
            allOccurrencesBySteps={props.isCurrentStageMode ? currentOccurrencesBySteps : allOccurrencesBySteps}
            finalizedOccurrencesBySteps={props.showFinalized ? finalizedOccurrencesBySteps : undefined}
            timePeriod={timePeriod}
            containerWidth={containerWidth}
            adjustDataWithMinHeight={adjustDataWithMinHeight}
            isResponsiveView={isResponsiveView}
            loading={props.loading}
            chartWidthMultiplier={chartWidthMultiplier}
            defaultChartMinWidth={defaultChartMinWidth}
            dynamicBarSize={dynamicBarSize}
            LEFT_LABEL_PAD={LEFT_LABEL_PAD}
            showQuantityChart={showQuantityChart}
            setShowQuantityChart={setShowQuantityChart}
            setStepFilter={props.setStepFilter}
            setChassisListFilterByStep={props.setChassisListFilterByStep}
            occurrenceStepAvailable={props.occurrenceStepAvailable}
            assetIds={props.assetIds}
            selectedMetric={selectedMetric}
            setTimePeriod={setTimePeriod}
            finishedFilter={props.finishedFilter}
            divideByUniqueChassis
            isCurrentStageMode={props.isCurrentStageMode}
            setCurrentStepFilter={props.setCurrentStepFilter}
          />
        )}
      </div>
    </>
  );
};
